import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Layers,
  Clock,
  AlertCircle,
  Save,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Bell,
  Mail,
  Upload,
  X,
  RotateCcw,
} from 'lucide-react';

// Default colors matching the quote's Color step (excluding "Other"
// custom input which admins don't track).
const DEFAULT_COLORS = ['Black', 'White', 'Grey', 'Red', 'Blue'];

const defaultInventoryRow = (id) => ({
  material_id: id,
  available: true,
  more_coming: false,
  restock_weeks: null,
  colors: DEFAULT_COLORS.map((name) => ({
    name,
    available: true,
    more_coming: false,
    restock_weeks: null,
  })),
});

// `rows` come from the backend — defaults merged with admin overrides.
// Only the per-material inventory (availability flags) and waitlist counts
// are stitched in locally.
const hydrateRows = (catalog, inventory, waitlistCounts) => {
  const invById = new Map(inventory.map((i) => [i.material_id, i]));

  return catalog.map((cat) => {
    const inv = invById.get(cat.material_id) || defaultInventoryRow(cat.material_id);

    // Merge saved colors with default palette so admins always see the full set.
    const existingByName = new Map(
      (inv.colors || []).map((c) => [c.name.toLowerCase(), c])
    );
    const mergedColors = DEFAULT_COLORS.map((name) => {
      const saved = existingByName.get(name.toLowerCase());
      return saved
        ? {
            name,
            available: Boolean(saved.available),
            more_coming: Boolean(saved.more_coming),
            restock_weeks: saved.restock_weeks ?? null,
          }
        : { name, available: true, more_coming: false, restock_weeks: null };
    });

    return {
      material_id: cat.material_id,
      name: cat.name || cat.material_id,
      subtitle: cat.subtitle || '',
      best_for: cat.best_for || '',
      thumbnail_url: cat.thumbnail_url || '',
      material_cost_adjustment_factor:
        typeof cat.material_cost_adjustment_factor === 'number'
          ? cat.material_cost_adjustment_factor
          : 1.0,
      cost_per_gram: cat.cost_per_gram ?? null,
      density: cat.density ?? null,
      sort_order: cat.sort_order ?? 999,
      is_default: Boolean(cat.is_default),
      has_override: Boolean(cat.has_override),
      available: Boolean(inv.available),
      more_coming: Boolean(inv.more_coming),
      restock_weeks: inv.restock_weeks ?? null,
      colors: mergedColors,
      waitlist_count: waitlistCounts[cat.material_id] || 0,
    };
  });
};

const blankCardForm = {
  material_id: '',
  name: '',
  subtitle: '',
  best_for: '',
  thumbnail_url: '',
  material_cost_adjustment_factor: 1.0,
  cost_per_gram: '',
  density: '',
};

const MaterialInventoryPanel = ({ makeAuthenticatedRequest }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState({});
  const [expanded, setExpanded] = useState({});
  const [waitlistModal, setWaitlistModal] = useState(null); // { material_id, entries, loading }

  // Add / Edit card dialog state
  const [cardDialog, setCardDialog] = useState(null); // null | { mode, data, originalId }
  const [uploadingThumb, setUploadingThumb] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, catRes, wlRes] = await Promise.all([
        makeAuthenticatedRequest('/api/admin/material-inventory'),
        makeAuthenticatedRequest('/api/admin/materials-catalog'),
        makeAuthenticatedRequest('/api/admin/materials/waitlist/summary'),
      ]);
      const inv = invRes.ok ? (await invRes.json()).materials || [] : [];
      const cat = catRes.ok ? (await catRes.json()).materials || [] : [];
      const wl = wlRes.ok ? (await wlRes.json()).counts || {} : {};
      setRows(hydrateRows(cat, inv, wl));
    } catch (err) {
      console.error('Load material inventory error:', err);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.material_id === id ? { ...r, ...patch } : r)));

  const updateColor = (materialId, colorName, patch) =>
    setRows((prev) =>
      prev.map((r) =>
        r.material_id === materialId
          ? {
              ...r,
              colors: r.colors.map((c) => (c.name === colorName ? { ...c, ...patch } : c)),
            }
          : r
      )
    );

  const saveInventory = async (row) => {
    setSavingIds((s) => ({ ...s, [row.material_id]: true }));
    try {
      const payload = {
        available: row.available,
        more_coming: row.more_coming,
        restock_weeks:
          row.more_coming && row.restock_weeks !== null ? Number(row.restock_weeks) : null,
        colors: row.colors.map((c) => ({
          name: c.name,
          available: c.available,
          more_coming: c.more_coming,
          restock_weeks:
            c.more_coming && c.restock_weeks !== null ? Number(c.restock_weeks) : null,
        })),
      };
      const res = await makeAuthenticatedRequest(
        `/api/admin/material-inventory/${encodeURIComponent(row.material_id)}`,
        { method: 'PUT', body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        alert(`Failed to save ${row.material_id}`);
      } else {
        // Reload so waitlist counter clears after a back-in-stock trigger.
        load();
      }
    } catch (err) {
      alert('Network error while saving');
    } finally {
      setSavingIds((s) => ({ ...s, [row.material_id]: false }));
    }
  };

  // --- Add / Edit card dialog ---
  const openAddDialog = () =>
    setCardDialog({ mode: 'add', data: { ...blankCardForm }, originalId: null });

  const openEditDialog = (row) =>
    setCardDialog({
      mode: 'edit',
      originalId: row.material_id,
      data: {
        material_id: row.material_id,
        name: row.name,
        subtitle: row.subtitle,
        best_for: row.best_for,
        thumbnail_url: row.thumbnail_url,
        material_cost_adjustment_factor: row.material_cost_adjustment_factor,
        cost_per_gram: row.cost_per_gram ?? '',
        density: row.density ?? '',
      },
    });

  const closeDialog = () => setCardDialog(null);

  const uploadThumbnail = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be 5MB or smaller');
      return;
    }
    setUploadingThumb(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await makeAuthenticatedRequest(
        '/api/admin/materials-catalog/upload-thumbnail',
        { method: 'POST', body }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || 'Upload failed');
        return;
      }
      setCardDialog((d) => ({
        ...d,
        data: { ...d.data, thumbnail_url: data.url },
      }));
    } catch (err) {
      alert('Network error while uploading thumbnail');
    } finally {
      setUploadingThumb(false);
    }
  };

  const saveCardDialog = async () => {
    const { mode, data } = cardDialog;
    const id = (data.material_id || '').trim();
    if (!id) {
      alert('Material ID is required');
      return;
    }
    if (mode === 'add' && rows.some((r) => r.material_id === id)) {
      alert('A material with that ID already exists');
      return;
    }
    const mcaf = Number(data.material_cost_adjustment_factor);
    if (!Number.isFinite(mcaf) || mcaf < 0.1 || mcaf > 10) {
      alert('Material Cost Adjustment Factor must be between 0.1 and 10');
      return;
    }

    // Optional fields — empty means "use defaults" (i.e., null)
    let costPerGram = null;
    if (data.cost_per_gram !== '' && data.cost_per_gram !== null && data.cost_per_gram !== undefined) {
      costPerGram = Number(data.cost_per_gram);
      if (!Number.isFinite(costPerGram) || costPerGram < 0.001 || costPerGram > 5.0) {
        alert('Cost per gram must be between $0.001 and $5.00');
        return;
      }
    }
    let density = null;
    if (data.density !== '' && data.density !== null && data.density !== undefined) {
      density = Number(data.density);
      if (!Number.isFinite(density) || density < 0.5 || density > 5.0) {
        alert('Density must be between 0.5 and 5.0 g/cm³');
        return;
      }
    }

    try {
      const res = await makeAuthenticatedRequest(
        `/api/admin/materials-catalog/${encodeURIComponent(id)}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            name: data.name || id,
            subtitle: data.subtitle || '',
            best_for: data.best_for || '',
            thumbnail_url: data.thumbnail_url || '',
            material_cost_adjustment_factor: mcaf,
            cost_per_gram: costPerGram,
            density: density,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to save material');
        return;
      }
      closeDialog();
      load();
    } catch (err) {
      alert('Network error while saving material');
    }
  };

  const deleteMaterial = async (row) => {
    if (
      !window.confirm(
        `Delete material "${row.name}" from the catalog?\n\n` +
          `This removes it from the Instant Quote dropdown and clears its ` +
          `inventory flags. Existing quotes referencing it are unaffected.`
      )
    )
      return;
    try {
      const res = await makeAuthenticatedRequest(
        `/api/admin/materials-catalog/${encodeURIComponent(row.material_id)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        alert('Failed to delete material');
        return;
      }
      load();
    } catch (err) {
      alert('Network error while deleting');
    }
  };

  const restoreDefaults = async (row) => {
    if (
      !window.confirm(
        `Restore defaults for "${row.name}"?\n\n` +
          `This wipes your custom edits (title, subtitle, best-for, ` +
          `thumbnail, MCAF) and reverts this material to the shipped ` +
          `baseline. Inventory flags (available / more coming) are kept.`
      )
    )
      return;
    try {
      const res = await makeAuthenticatedRequest(
        `/api/admin/materials-catalog/${encodeURIComponent(row.material_id)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        alert('Failed to restore defaults');
        return;
      }
      load();
    } catch (err) {
      alert('Network error while restoring defaults');
    }
  };

  // --- Waitlist modal ---
  const openWaitlistModal = async (row) => {
    setWaitlistModal({ material_id: row.material_id, name: row.name, entries: [], loading: true });
    try {
      const res = await makeAuthenticatedRequest(
        `/api/admin/materials/${encodeURIComponent(row.material_id)}/waitlist`
      );
      if (res.ok) {
        const data = await res.json();
        setWaitlistModal({
          material_id: row.material_id,
          name: row.name,
          entries: data.entries || [],
          loading: false,
        });
      } else {
        setWaitlistModal({
          material_id: row.material_id,
          name: row.name,
          entries: [],
          loading: false,
        });
      }
    } catch (err) {
      setWaitlistModal((m) => (m ? { ...m, loading: false } : null));
    }
  };

  const badgeFor = (row) => {
    if (!row.available && row.more_coming) {
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
          <Clock className="w-3 h-3 mr-1" />
          More Coming
        </Badge>
      );
    }
    if (!row.available) {
      return (
        <Badge variant="destructive" className="text-xs">
          <AlertCircle className="w-3 h-3 mr-1" />
          Out of Stock
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">Available</Badge>
    );
  };

  const sortedRows = useMemo(() => rows, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        Loading material inventory…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="material-inventory-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Layers className="w-6 h-6 text-[#0007b5]" />
            Material Inventory
          </h2>
          <p className="text-slate-600 mt-1">
            Control availability, edit material cards, and manage the{' '}
            <span className="font-medium text-amber-700">More Coming</span> restock ETA.
            Out-of-stock materials remain visible in the Instant Quote dropdown so customers
            can sign up to be notified.
          </p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#0007b5] hover:bg-[#000690]"
          data-testid="material-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Material
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Materials</CardTitle>
          <CardDescription>
            Toggle availability, tune the Material Cost Adjustment Factor used in quote pricing,
            and respond to customer back-in-stock requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedRows.map((row) => {
            const isSaving = Boolean(savingIds[row.material_id]);
            const isExpanded = Boolean(expanded[row.material_id]);
            return (
              <div
                key={row.material_id}
                className="border rounded-lg p-4 bg-white"
                data-testid={`material-row-${row.material_id}`}
              >
                <div className="flex flex-wrap items-start gap-4">
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden border">
                    {row.thumbnail_url ? (
                      <img
                        src={
                          row.thumbnail_url.startsWith('http')
                            ? row.thumbnail_url
                            : `${process.env.REACT_APP_BACKEND_URL || ''}${row.thumbnail_url}`
                        }
                        alt={row.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Layers className="w-6 h-6" />
                      </div>
                    )}
                  </div>

                  {/* Name + meta */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{row.name}</span>
                      {row.subtitle && (
                        <span className="text-xs text-slate-500">{row.subtitle}</span>
                      )}
                      {row.is_default && row.has_override && (
                        <Badge
                          className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] uppercase tracking-wide"
                          data-testid={`material-edited-badge-${row.material_id}`}
                        >
                          Edited
                        </Badge>
                      )}
                      {!row.is_default && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] uppercase tracking-wide">
                          Custom
                        </Badge>
                      )}
                      {badgeFor(row)}
                      {row.waitlist_count > 0 && (
                        <button
                          type="button"
                          onClick={() => openWaitlistModal(row)}
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5 hover:bg-blue-100"
                          data-testid={`material-waitlist-badge-${row.material_id}`}
                        >
                          <Bell className="w-3 h-3" />
                          {row.waitlist_count} waiting
                        </button>
                      )}
                    </div>
                    {row.best_for && (
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="uppercase tracking-wide text-[10px] text-slate-400">
                          Best for
                        </span>{' '}
                        {row.best_for}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">
                      <span className="uppercase tracking-wide text-[10px] text-slate-400">MCAF</span>{' '}
                      {row.material_cost_adjustment_factor.toFixed(2)}×
                    </p>
                  </div>

                  {/* Edit card / Restore / Delete */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(row)}
                      data-testid={`material-edit-card-${row.material_id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit Card
                    </Button>
                    {row.is_default && row.has_override && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        onClick={() => restoreDefaults(row)}
                        title="Wipe custom edits and revert to shipped defaults"
                        data-testid={`material-restore-defaults-${row.material_id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restore defaults
                      </Button>
                    )}
                    {!row.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => deleteMaterial(row)}
                        data-testid={`material-delete-${row.material_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Availability controls */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.available}
                      onChange={(e) =>
                        updateRow(row.material_id, {
                          available: e.target.checked,
                          more_coming: e.target.checked ? false : row.more_coming,
                        })
                      }
                      className="w-4 h-4 accent-[#0007b5] cursor-pointer"
                      data-testid={`material-available-${row.material_id}`}
                    />
                    Available
                  </label>

                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.more_coming && !row.available}
                      disabled={row.available}
                      onChange={(e) =>
                        updateRow(row.material_id, { more_coming: e.target.checked })
                      }
                      className="w-4 h-4 accent-amber-500 cursor-pointer disabled:cursor-not-allowed"
                      data-testid={`material-more-coming-${row.material_id}`}
                    />
                    More Coming
                  </label>

                  {row.more_coming && !row.available && (
                    <div className="flex items-center gap-2 text-sm">
                      <Input
                        type="number"
                        min="1"
                        max="52"
                        value={row.restock_weeks ?? ''}
                        onChange={(e) =>
                          updateRow(row.material_id, {
                            restock_weeks: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        placeholder="weeks"
                        className="w-20 h-8"
                        data-testid={`material-restock-weeks-${row.material_id}`}
                      />
                      <span className="text-slate-500">weeks</span>
                    </div>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [row.material_id]: !e[row.material_id] }))
                    }
                  >
                    {isExpanded ? 'Hide colors' : 'Colors'}
                  </Button>

                  <Button
                    size="sm"
                    onClick={() => saveInventory(row)}
                    disabled={isSaving}
                    className="bg-[#0007b5] hover:bg-[#000690] ml-auto"
                    data-testid={`material-save-${row.material_id}`}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Saving
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-600 mb-3">
                      Color availability for {row.name}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {row.colors.map((c) => (
                        <div
                          key={c.name}
                          className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-md bg-slate-50 text-sm"
                        >
                          <span className="font-medium w-14 flex-shrink-0">{c.name}</span>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={c.available}
                              onChange={(e) =>
                                updateColor(row.material_id, c.name, {
                                  available: e.target.checked,
                                  more_coming: e.target.checked ? false : c.more_coming,
                                })
                              }
                              className="w-4 h-4 accent-[#0007b5]"
                            />
                            Available
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={c.more_coming && !c.available}
                              disabled={c.available}
                              onChange={(e) =>
                                updateColor(row.material_id, c.name, {
                                  more_coming: e.target.checked,
                                })
                              }
                              className="w-4 h-4 accent-amber-500 disabled:cursor-not-allowed"
                            />
                            More Coming
                          </label>
                          {c.more_coming && !c.available && (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="1"
                                max="52"
                                value={c.restock_weeks ?? ''}
                                onChange={(e) =>
                                  updateColor(row.material_id, c.name, {
                                    restock_weeks:
                                      e.target.value === '' ? null : Number(e.target.value),
                                  })
                                }
                                placeholder="wks"
                                className="w-16 h-7"
                              />
                              <span className="text-xs text-slate-500">weeks</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Add / Edit material card dialog */}
      <Dialog open={!!cardDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-lg bg-white" data-testid="material-card-dialog">
          <DialogHeader>
            <DialogTitle>
              {cardDialog?.mode === 'add' ? 'Add new material' : `Edit ${cardDialog?.data?.name}`}
            </DialogTitle>
            <DialogDescription>
              Changes appear in the Instant Quote material picker immediately after saving.
            </DialogDescription>
          </DialogHeader>
          {cardDialog && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Material ID
                </label>
                <Input
                  value={cardDialog.data.material_id}
                  onChange={(e) =>
                    setCardDialog((d) => ({
                      ...d,
                      data: { ...d.data, material_id: e.target.value },
                    }))
                  }
                  placeholder="e.g. PLA, PETG-CF"
                  disabled={cardDialog.mode === 'edit'}
                  data-testid="material-card-field-id"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Unique identifier — must match the value stored on historical quotes if you're
                  editing an existing material.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Title
                </label>
                <Input
                  value={cardDialog.data.name}
                  onChange={(e) =>
                    setCardDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))
                  }
                  placeholder="Display name"
                  data-testid="material-card-field-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Subtitle
                </label>
                <Input
                  value={cardDialog.data.subtitle}
                  onChange={(e) =>
                    setCardDialog((d) => ({
                      ...d,
                      data: { ...d.data, subtitle: e.target.value },
                    }))
                  }
                  placeholder="Short descriptor shown next to the title"
                  data-testid="material-card-field-subtitle"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Best For
                </label>
                <Input
                  value={cardDialog.data.best_for}
                  onChange={(e) =>
                    setCardDialog((d) => ({
                      ...d,
                      data: { ...d.data, best_for: e.target.value },
                    }))
                  }
                  placeholder="e.g. Functional parts, bushings, joints"
                  data-testid="material-card-field-best-for"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Thumbnail
                </label>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <div className="w-20 h-20 rounded-lg overflow-hidden border bg-slate-50 flex-shrink-0">
                    {cardDialog.data.thumbnail_url ? (
                      <img
                        src={
                          cardDialog.data.thumbnail_url.startsWith('http')
                            ? cardDialog.data.thumbnail_url
                            : `${process.env.REACT_APP_BACKEND_URL || ''}${cardDialog.data.thumbnail_url}`
                        }
                        alt="Material thumbnail preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Layers className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      {uploadingThumb ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading…
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          {cardDialog.data.thumbnail_url ? 'Replace image' : 'Upload image'}
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        disabled={uploadingThumb}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadThumbnail(file);
                          e.target.value = '';
                        }}
                        data-testid="material-card-thumbnail-upload"
                      />
                    </label>
                    {cardDialog.data.thumbnail_url && (
                      <button
                        type="button"
                        onClick={() =>
                          setCardDialog((d) => ({
                            ...d,
                            data: { ...d.data, thumbnail_url: '' },
                          }))
                        }
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline self-start"
                      >
                        <X className="w-3 h-3" />
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-slate-500">
                      JPG, PNG, WEBP, or GIF · 5MB max
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Material Cost Adjustment Factor (MCAF)
                </label>
                <Input
                  type="number"
                  step="0.05"
                  min="0.1"
                  max="10"
                  value={cardDialog.data.material_cost_adjustment_factor}
                  onChange={(e) =>
                    setCardDialog((d) => ({
                      ...d,
                      data: {
                        ...d.data,
                        material_cost_adjustment_factor: e.target.value,
                      },
                    }))
                  }
                  data-testid="material-card-field-mcaf"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Multiplier applied to the base print cost during quoting. 1.00× = no
                  adjustment, 1.50× = +50%, etc. Range 0.1–10.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Cost per gram ($)
                  </label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0.001"
                    max="5.0"
                    value={cardDialog.data.cost_per_gram}
                    onChange={(e) =>
                      setCardDialog((d) => ({
                        ...d,
                        data: { ...d.data, cost_per_gram: e.target.value },
                      }))
                    }
                    placeholder="leave blank for default"
                    data-testid="material-card-field-cost-per-gram"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Filament base cost (e.g. 0.025 = $25/kg). Range 0.001–5.0.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Density (g/cm³)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.5"
                    max="5.0"
                    value={cardDialog.data.density}
                    onChange={(e) =>
                      setCardDialog((d) => ({
                        ...d,
                        data: { ...d.data, density: e.target.value },
                      }))
                    }
                    placeholder="leave blank for default"
                    data-testid="material-card-field-density"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used in the weight×density×infill formula. Range 0.5–5.0 g/cm³.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={saveCardDialog}
              className="bg-[#0007b5] hover:bg-[#000690]"
              data-testid="material-card-dialog-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waitlist modal */}
      <Dialog open={!!waitlistModal} onOpenChange={(open) => !open && setWaitlistModal(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="material-waitlist-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#0007b5]" />
              Waitlist for {waitlistModal?.name}
            </DialogTitle>
            <DialogDescription>
              Subscribers who'll be emailed when you flip this material back to Available.
            </DialogDescription>
          </DialogHeader>
          {waitlistModal?.loading ? (
            <div className="py-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : waitlistModal?.entries?.length ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {waitlistModal.entries.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between gap-3 border border-slate-200 rounded-md p-3 text-sm"
                >
                  <div>
                    <div className="flex items-center gap-2 font-medium text-slate-900">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {e.email}
                    </div>
                    {e.name && <div className="text-xs text-slate-500">{e.name}</div>}
                    <div className="text-xs text-slate-400">
                      Joined {new Date(e.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {e.notified ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">
                      Notified
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                      Pending
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-slate-500">No subscribers yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialInventoryPanel;
